import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { useProcgen } from '../../state/procgen.js';
import { useZoomPan } from '../generation/previews/useZoomPan.js';
import { IconButton, NumberField, SelectField, TextField, Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import {
  defaultOutput,
  instanceName,
  outputRefs,
  type PreviewArea,
  type PreviewPath,
  type PreviewPoint,
  type PreviewResult,
  type PreviewSettings,
  type Slice,
} from './procgen-doc.js';

/**
 * The preview column of the Procedural panel (section 10.4): output /
 * expression picker, rect, size, seed and slice controls, overlay toggles
 * with per-overlay filters, the image (base64 RGBA → putImageData) with zoom
 * and pan (useZoomPan) and an SVG overlay for points, areas and paths, a
 * hover probe, the legend and a stats line. Rows fill in progressively while
 * the engine answers `done: false`.
 */

const SIZES = ['128', '256', '512', '1024'];
const RAMPS = ['auto', 'gray', 'heat', 'terrain'];

function rgb(c: readonly number[] | null | undefined, fallback = '#ffd166'): string {
  if (!Array.isArray(c) || c.length < 3) return fallback;
  const ch = (v: number | undefined) => Math.round(Math.min(1, Math.max(0, v ?? 0)) * 255);
  return `rgb(${ch(c[0])} ${ch(c[1])} ${ch(c[2])})`;
}

/** World point of an item on the slice plane. */
function planeOf(slice: Slice, p: { x: number; y: number; z: number }): [number, number] {
  if (slice === 'xy') return [p.x, p.y];
  if (slice === 'zy') return [p.z, p.y];
  return [p.x, p.z];
}

interface Mapper {
  /** World (plane) → content pixels. */
  px: (a: number, b: number) => [number, number];
  /** Content pixels → world (plane). */
  world: (x: number, y: number) => [number, number];
  /** World length → pixels (along the first axis). */
  len: (v: number) => number;
}

function mapper(rect: readonly number[], w: number, h: number): Mapper {
  const [a0 = 0, b0 = 0, a1 = 1, b1 = 1] = rect;
  const sa = w / (a1 - a0 || 1);
  const sb = h / (b1 - b0 || 1);
  return {
    px: (a, b) => [(a - a0) * sa, (b - b0) * sb],
    world: (x, y) => [a0 + x / sa, b0 + y / sb],
    len: (v) => v * sa,
  };
}

function AreaShape({ area, m, scale }: { area: PreviewArea; m: Mapper; scale: number }) {
  const stroke = 1.5 / scale;
  if (area.circle) {
    const [x, z, r] = area.circle;
    const [cx, cy] = m.px(x, z);
    return <circle className="pcg-ov-area" cx={cx} cy={cy} r={m.len(r)} strokeWidth={stroke} />;
  }
  if (Array.isArray(area.rect) && area.rect.length >= 4) {
    const [cx = 0, cz = 0, rw = 0, rd = 0] = area.rect;
    const [x, y] = m.px(cx, cz);
    const pw = m.len(rw);
    const ph = m.len(rd);
    const yaw = typeof area.yaw === 'number' ? area.yaw : 0;
    return <rect className="pcg-ov-area" x={x - pw / 2} y={y - ph / 2} width={pw} height={ph} transform={`rotate(${yaw} ${x} ${y})`} strokeWidth={stroke} />;
  }
  if (Array.isArray(area.polygon)) {
    const pts = area.polygon.map(([a, b]) => m.px(a, b).join(',')).join(' ');
    return <polygon className="pcg-ov-area" points={pts} strokeWidth={stroke} />;
  }
  return null;
}

function PathLine({ path, m, scale }: { path: PreviewPath; m: Mapper; scale: number }) {
  if (!Array.isArray(path.points) || path.points.length < 2) return null;
  const pts = path.points.map(([a, b]) => m.px(a, b).join(',')).join(' ');
  const width = typeof path.width === 'number' ? Math.max(1.5 / scale, m.len(path.width)) : 2 / scale;
  return <polyline className="pcg-ov-path" points={pts} strokeWidth={width} />;
}

/** Canvas + overlay with zoom / pan and the hover probe. */
export function PreviewCanvas({ result, image, version, settings }: { result: PreviewResult | null; image: Uint8ClampedArray | null; version: number; settings: PreviewSettings }) {
  const probeAt = useProcgen((s) => s.probeAt);
  const probe = useProcgen((s) => s.probe);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const size: [number, number] = result?.size && Array.isArray(result.size) ? result.size : settings.size;
  const [w, h] = size;
  const rect: readonly number[] = result?.rect && Array.isArray(result.rect) ? result.rect : settings.rect;
  const content = useMemo(() => ({ width: w, height: h }), [w, h]);
  const zoom = useZoomPan({ content });
  const { view } = zoom;
  const m = mapper(rect, w, h);
  const [hover, setHover] = useState<{ sx: number; sy: number; x: number; z: number; item: PreviewPoint | null } | null>(null);
  const probeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    if (!image || image.length !== w * h * 4) {
      ctx.clearRect(0, 0, w, h);
      return;
    }
    const data = ctx.createImageData(w, h);
    data.data.set(image);
    ctx.putImageData(data, 0, 0);
  }, [image, version, w, h]);

  useEffect(() => () => {
    if (probeTimer.current) clearTimeout(probeTimer.current);
  }, []);

  const points = result?.points ?? [];

  function onMove(e: ReactPointerEvent<HTMLDivElement>): void {
    zoom.boxProps.onPointerMove(e);
    if (zoom.dragging) return;
    const box = zoom.boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const cx = (sx - view.x) / view.scale;
    const cy = (sy - view.y) / view.scale;
    if (cx < 0 || cy < 0 || cx > w || cy > h) {
      setHover(null);
      return;
    }
    const [x, z] = m.world(cx, cy);
    // Nearest overlay point within 6 screen pixels.
    let item: PreviewPoint | null = null;
    let best = (6 / view.scale) ** 2;
    for (const p of points) {
      const [a, b] = m.px(...planeOf(settings.slice, p));
      const d = (a - cx) ** 2 + (b - cy) ** 2;
      if (d < best) {
        best = d;
        item = p;
      }
    }
    setHover({ sx, sy, x, z, item });
    if (probeTimer.current) clearTimeout(probeTimer.current);
    probeTimer.current = setTimeout(() => void probeAt(x, z), 300);
  }

  const dot = 3 / view.scale;
  const hovered = hover?.item ?? null;
  const hoveredAt = hovered ? m.px(...planeOf(settings.slice, hovered)) : [0, 0];
  return (
    <div
      ref={zoom.boxRef}
      className={`pcg-canvas-box${zoom.dragging ? ' is-dragging' : ''}`}
      role="group"
      aria-label="Procedural preview. Scroll to zoom, drag to pan, double click to fit."
      {...zoom.boxProps}
      onPointerMove={onMove}
      onPointerLeave={() => setHover(null)}
    >
      <div className="pcg-canvas-world" style={{ width: w, height: h, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
        <canvas ref={canvasRef} width={w} height={h} className="pcg-canvas" />
        <svg className="pcg-overlay" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
          {(result?.areas ?? []).map((a) => (
            <AreaShape key={`${a.ref}#${a.id}`} area={a} m={m} scale={view.scale} />
          ))}
          {(result?.paths ?? []).map((p) => (
            <PathLine key={`${p.ref}#${p.id}`} path={p} m={m} scale={view.scale} />
          ))}
          {points.map((p) => {
            const [cx, cy] = m.px(...planeOf(settings.slice, p));
            return <circle key={`${p.ref}#${p.id}`} className="pcg-ov-point" cx={cx} cy={cy} r={dot} fill={rgb(p.color)} strokeWidth={0.75 / view.scale} />;
          })}
          {hovered && <circle className="pcg-ov-hover" cx={hoveredAt[0]} cy={hoveredAt[1]} r={dot * 2.2} strokeWidth={1.5 / view.scale} />}
        </svg>
      </div>
      <div className="pcg-viewbar" onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <IconButton icon="minus" title="Zoom out" size={13} onClick={() => zoom.zoomBy(0.8)} />
        <IconButton icon="plus" title="Zoom in" size={13} onClick={() => zoom.zoomBy(1.25)} />
        <IconButton icon="focus" title="Fit (double click)" size={13} onClick={zoom.fit} />
      </div>
      {hover && (
        <div className="pcg-probe" style={{ left: Math.min(hover.sx + 14, (zoom.boxRef.current?.clientWidth ?? 400) - 220), top: hover.sy + 14 }}>
          <div className="mono">
            {settings.slice === 'xz' ? 'x' : settings.slice === 'xy' ? 'x' : 'z'} {hover.x.toFixed(1)} · {settings.slice === 'xz' ? 'z' : 'y'} {hover.z.toFixed(1)}
          </div>
          {probe && Math.abs(probe.x - hover.x) < 1e-6 && Math.abs(probe.z - hover.z) < 1e-6 && probe.result && <ProbeValue probe={probe.result} />}
          {hover.item && (
            <div className="pcg-probe-item">
              <strong>{hover.item.ref}</strong> <span className="muted mono">{hover.item.id}</span>
              {hover.item.label && <div>label: {hover.item.label}</div>}
              {Object.entries(hover.item.attrs ?? {}).map(([k, v]) => (
                <div key={k} className="mono">
                  {k}: {typeof v === 'number' ? Math.round(v * 1000) / 1000 : JSON.stringify(v)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProbeValue({ probe }: { probe: NonNullable<PreviewResult['probe']> }) {
  const value = probe.value;
  const shown = typeof value === 'number' ? String(Math.round(value * 10000) / 10000) : value === undefined ? '' : JSON.stringify(value);
  return (
    <div className="pcg-probe-value">
      {shown && <div className="mono">value: {shown}</div>}
      {probe.label && <div>label: {probe.label}</div>}
      {probe.weights &&
        Object.entries(probe.weights)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([k, v]) => (
            <div key={k} className="mono">
              {k}: {Math.round(v * 1000) / 1000}
            </div>
          ))}
    </div>
  );
}

/** The whole right column: controls, canvas, legend and stats. */
export function PreviewPane() {
  const path = useProcgen((s) => s.path);
  const doc = useProcgen((s) => s.doc);
  const settings = useProcgen((s) => s.settings);
  const preview = useProcgen((s) => s.preview);
  const autoPreview = useProcgen((s) => s.autoPreview);
  const setSettings = useProcgen((s) => s.setSettings);
  const setAutoPreview = useProcgen((s) => s.setAutoPreview);
  const runPreview = useProcgen((s) => s.runPreview);
  const pinSettings = useProcgen((s) => s.pinSettings);
  const [extraOverlay, setExtraOverlay] = useState('');

  if (!path || !doc) return <div className="pcg-preview muted pcg-empty-note">Open a generator to preview it.</div>;

  const instance = instanceName(doc, path);
  const refs = outputRefs(doc, instance);
  const overlayRefs = [...new Set([...outputRefs(doc, instance, ['points', 'areas', 'paths']), ...settings.overlays])];
  const out = defaultOutput(doc);
  const placeholder = out ? `${instance}.${out}` : 'output or expression';
  const r = preview.result;
  const stats = r?.stats;
  const [x0, z0, x1, z1] = settings.rect;

  return (
    <div className="pcg-preview">
      <div className="pcg-preview-controls">
        <div className="pcg-ctl-row">
          <span className="pcg-ctl-label" title="An output reference or any expression (a bool expression shows where it is true)">
            Show
          </span>
          <TextField value={settings.show} placeholder={placeholder} list="pcg-show-refs" mono onChange={(v) => setSettings({ show: v.trim(), scope: v.trim() && !refs.includes(v.trim()) ? instance : null })} />
          <datalist id="pcg-show-refs">
            {refs.map((ref) => (
              <option key={ref} value={ref} />
            ))}
          </datalist>
          {settings.show && <IconButton icon="x" title="Show the default output" size={12} onClick={() => setSettings({ show: '', scope: null })} />}
        </div>
        <div className="pcg-ctl-row">
          <span className="pcg-ctl-label" title="[x0, z0, x1, z1] in meters">
            Rect
          </span>
          <NumberField label="x0" value={x0} step={8} onChange={(v) => setSettings({ rect: [v, z0, x1, z1] })} />
          <NumberField label="z0" value={z0} step={8} onChange={(v) => setSettings({ rect: [x0, v, x1, z1] })} />
          <NumberField label="x1" value={x1} step={8} onChange={(v) => setSettings({ rect: [x0, z0, v, z1] })} />
          <NumberField label="z1" value={z1} step={8} onChange={(v) => setSettings({ rect: [x0, z0, x1, v] })} />
        </div>
        <div className="pcg-ctl-row">
          <span className="pcg-ctl-label">Size</span>
          <SelectField value={String(settings.size[0])} options={SIZES.includes(String(settings.size[0])) ? SIZES : [...SIZES, String(settings.size[0])]} onChange={(v) => setSettings({ size: [Number(v), Number(v)] })} />
          <span className="pcg-ctl-label">Seed</span>
          <NumberField value={settings.seed} step={1} min={0} integer onChange={(v) => setSettings({ seed: v })} />
          <IconButton icon="refresh" title="Random seed" size={12} onClick={() => setSettings({ seed: Math.floor(Math.random() * 1e9) })} />
        </div>
        <div className="pcg-ctl-row">
          <span className="pcg-ctl-label">Slice</span>
          <SelectField value={settings.slice} options={['xz', 'xy', 'zy']} onChange={(v) => setSettings({ slice: v as Slice })} />
          <NumberField label="y" value={settings.y} step={1} onChange={(v) => setSettings({ y: v })} />
          <SelectField value={settings.ramp} options={RAMPS} onChange={(v) => setSettings({ ramp: v })} />
          <SelectField value={settings.weights} options={['blend', 'dominant']} onChange={(v) => setSettings({ weights: v === 'dominant' ? 'dominant' : 'blend' })} />
        </div>
        <div className="pcg-ctl-row">
          <span className="pcg-ctl-label" title="Multiply by hillshade from this height field">
            Shade
          </span>
          <TextField value={settings.shade} placeholder="terrain.height" list="pcg-show-refs" mono onChange={(v) => setSettings({ shade: v.trim() })} />
        </div>
        <div className="pcg-overlays">
          <div className="pcg-subhead">Overlays</div>
          {overlayRefs.length === 0 && <div className="muted pcg-empty-note">No points, areas or paths outputs.</div>}
          {overlayRefs.map((ref) => {
            const on = settings.overlays.includes(ref);
            return (
              <div key={ref} className="pcg-overlay-row">
                <Toggle
                  label={ref}
                  checked={on}
                  onChange={(v) => setSettings({ overlays: v ? [...settings.overlays, ref] : settings.overlays.filter((o) => o !== ref) })}
                />
                {on && (
                  <TextField
                    value={settings.overlayWhere[ref] ?? ''}
                    placeholder="filter: it.age > 40"
                    mono
                    onChange={(v) => {
                      const where = { ...settings.overlayWhere };
                      if (v.trim()) where[ref] = v.trim();
                      else delete where[ref];
                      setSettings({ overlayWhere: where });
                    }}
                  />
                )}
              </div>
            );
          })}
          <div className="pcg-overlay-row">
            <input className="textfield mono" value={extraOverlay} placeholder="other set: nature.trees" onChange={(e) => setExtraOverlay(e.target.value)} aria-label="Add overlay reference" />
            <button
              type="button"
              className="pcg-btn"
              disabled={!extraOverlay.trim()}
              onClick={() => {
                setSettings({ overlays: [...new Set([...settings.overlays, extraOverlay.trim()])] });
                setExtraOverlay('');
              }}
            >
              <Icon name="plus" size={11} /> Overlay
            </button>
          </div>
        </div>
        <div className="pcg-ctl-row">
          <button type="button" className="pcg-btn is-primary" onClick={() => void runPreview()}>
            <Icon name="play" size={11} /> Preview
          </button>
          <Toggle label="Live" checked={autoPreview} onChange={setAutoPreview} />
          <button type="button" className="pcg-btn is-quiet" title="Save these preview settings in the generator (editor.preview)" onClick={pinSettings}>
            Pin view
          </button>
          <span className={`pcg-status is-${preview.status}`}>
            {preview.status === 'running' ? 'Rendering…' : preview.status === 'queued' ? 'Queued' : preview.status === 'error' ? 'Failed' : preview.status === 'done' ? `Done${preview.ms !== null ? ` in ${preview.ms} ms` : ''}` : ''}
            {preview.source && <span className="muted"> · {preview.source}</span>}
          </span>
        </div>
      </div>
      {preview.error && <div className="pcg-error">{preview.error}</div>}
      {preview.warnings.map((w, i) => (
        <div key={i} className="pcg-warning">
          {w}
        </div>
      ))}
      <PreviewCanvas result={r} image={preview.image} version={preview.version} settings={settings} />
      {r?.legend && r.legend.length > 0 && (
        <div className="pcg-legend">
          {r.legend.map((l) => (
            <span key={l.name} className="pcg-legend-item">
              <span className="pcg-swatch" style={{ background: rgb(l.color, '#888') }} />
              {l.name}
              {typeof l.cover === 'number' && <span className="muted"> {Math.round(l.cover * 100)}%</span>}
            </span>
          ))}
        </div>
      )}
      {r && (
        <div className="pcg-stats mono">
          {r.kind && <span>{r.kind}</span>}
          {typeof r.min === 'number' && typeof r.max === 'number' && (
            <span>
              {Math.round(r.min * 1000) / 1000} … {Math.round(r.max * 1000) / 1000}
              {r.unit ? ` ${r.unit}` : ''}
            </span>
          )}
          {stats?.samples !== undefined && <span>{stats.samples} samples</span>}
          {stats?.costPerLane !== undefined && <span>{stats.costPerLane} ns/lane</span>}
          {stats?.points !== undefined && <span>{stats.points} points{stats.truncated ? ' (capped)' : ''}</span>}
          {!!stats?.clamped && <span className="is-warn">{stats.clamped} clamped</span>}
          {!!stats?.noneReplaced && <span className="is-warn">{stats.noneReplaced} none→default</span>}
          {!!stats?.divZero && <span className="is-warn">{stats.divZero} ÷0</span>}
          {!!stats?.pendingScripts && <span>{stats.pendingScripts} scripts pending</span>}
          {r.approximate && <span title="lod: auto evaluated lattice fields at pixel centres">approximate</span>}
          {r.done === false && <span>filling…</span>}
        </div>
      )}
      {preview.diagnostics.length > 0 && (
        <div className="pcg-preview-diags">
          {preview.diagnostics.slice(0, 8).map((d, i) => (
            <div key={i} className={`pcg-expr-msg is-${d.severity}`}>
              <span className="pcg-code">{d.code}</span> {d.path && <span className="mono">{d.path}: </span>}
              {d.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
